<template>
  <div class="bg-marketing-light pb-5">
    <!-- Breadcrumb Section -->
    <section class="bg-white py-3" style="border-bottom: 1px solid #E2E8F0;">
      <CContainer>
        <div class="d-flex align-items-center small text-muted">
          <router-link to="/" class="text-marketing-gray-600 font-weight-500" style="text-decoration: none;">Home</router-link>
          <CIcon name="cil-chevron-right" size="sm" class="mx-2"/>
          <router-link to="/calculators" class="text-marketing-gray-600 font-weight-500" style="text-decoration: none;">Calculators</router-link>
          <CIcon name="cil-chevron-right" size="sm" class="mx-2"/>
          <span class="text-marketing-dark font-weight-bold">EPF Calculator</span>
        </div>
      </CContainer>
    </section>

    <!-- Hero Section -->
    <section class="py-5 bg-white">
      <CContainer>
        <CRow class="justify-content-center">
          <CCol lg="8" class="text-center">
            <div class="marketing-icon-wrapper mx-auto mb-4" style="width: 80px; height: 80px; font-size: 2rem;">
              <CIcon name="cil-calculator" size="custom-size" style="width: 40px; height: 40px;"/>
            </div>
            <h1 class="display-4 font-weight-bold marketing-heading mb-3">
              EPF Calculator Malaysia 2026
            </h1>
            <p class="lead marketing-body mb-0">
              Free online EPF (KWSP) calculator for Malaysian employees and employers. Calculate your EPF contributions instantly with the latest rates.
            </p>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Main Calculator Section -->
    <section class="py-5">
      <CContainer>
        <CRow>
          <!-- Left Column: Interactive Calculator -->
          <CCol lg="8" class="mb-4">
            <CCard class="border-0 shadow-sm h-100">
              <CCardBody class="p-4 p-md-5">
                <h2 class="h3 font-weight-bold text-marketing-dark mb-4">Calculate EPF Contributions</h2>

                <!-- Input: Salary -->
                <div class="form-group mb-4">
                  <label class="font-weight-600 text-marketing-dark">Monthly Salary (RM)</label>
                  <div class="input-group input-group-lg">
                    <div class="input-group-prepend">
                      <span class="input-group-text bg-white border-right-0 text-muted font-weight-bold">RM</span>
                    </div>
                    <input 
                      type="number" 
                      class="form-control border-left-0 pl-0" 
                      v-model.number="salary"
                      @input="calculate"
                      placeholder="5000"
                      min="0"
                      step="100"
                      style="box-shadow: none;"
                    >
                  </div>
                  <small class="form-text text-muted mt-2">Enter your gross monthly salary before deductions</small>
                </div>

                <!-- Input: Age Range Buttons -->
                <div class="form-group mb-4">
                  <label class="font-weight-600 text-marketing-dark mb-3">Employee Age</label>
                  <CRow>
                    <CCol md="4" class="mb-2">
                       <CButton 
                         block
                         :color="ageRange === 'below60' ? 'success' : 'light'"
                         class="py-3 font-weight-bold"
                         style="border-width: 2px;"
                         :class="{'border-success': ageRange === 'below60'}"
                         @click="selectAge('below60')"
                       >
                         Below 60
                       </CButton>
                    </CCol>
                    <CCol md="4" class="mb-2">
                       <CButton 
                         block
                         :color="ageRange === '60to75' ? 'success' : 'light'"
                         class="py-3 font-weight-bold"
                         style="border-width: 2px;"
                         :class="{'border-success': ageRange === '60to75'}"
                         @click="selectAge('60to75')"
                       >
                         60 - 75
                       </CButton>
                    </CCol>
                    <CCol md="4" class="mb-2">
                       <CButton 
                         block
                         :color="ageRange === 'above75' ? 'success' : 'light'"
                         class="py-3 font-weight-bold"
                         style="border-width: 2px;"
                         :class="{'border-success': ageRange === 'above75'}"
                         @click="selectAge('above75')"
                       >
                         Above 75
                       </CButton>
                    </CCol>
                  </CRow>
                </div>

                <!-- Opt-out Checkbox for Below 60 -->
                <div v-if="salary > 0 && ageRange === 'below60'" class="alert alert-info border border-info mb-4" style="background-color: #f0f9ff;">
                  <div class="custom-control custom-checkbox">
                    <input type="checkbox" class="custom-control-input" id="optout" v-model="optOut5560" @change="calculate">
                    <label class="custom-control-label font-weight-500" style="color: #0c4a6e;" for="optout">
                      <strong>Age 55-60 Opt-Out:</strong> Employee has opted out of EPF contributions.
                    </label>
                  </div>
                </div>

                <!-- Results Panel (Active) -->
                <div v-if="salary > 0" class="bg-marketing-primary text-white p-4 rounded-lg mt-5" style="background: linear-gradient(135deg, #2E7D32 0%, #1b4d21 100%);">
                  <h4 class="h5 font-weight-bold mb-4 d-flex align-items-center">
                    <CIcon name="cil-check-circle" class="mr-2"/> Calculation Results
                  </h4>

                  <CRow class="mb-4">
                    <CCol md="4" class="mb-3 mb-md-0">
                      <div class="p-3 rounded" style="background: rgba(255,255,255,0.15);">
                        <div class="small opacity-75 mb-1 text-uppercase letter-spacing-1">Employee</div>
                        <div class="h3 font-weight-bold mb-0">RM {{ employeeEPF.toFixed(2) }}</div>
                        <div class="small mt-1 opacity-75">{{ employeeRate }}% of salary</div>
                      </div>
                    </CCol>
                    <CCol md="4" class="mb-3 mb-md-0">
                      <div class="p-3 rounded" style="background: rgba(255,255,255,0.15);">
                        <div class="small opacity-75 mb-1 text-uppercase letter-spacing-1">Employer</div>
                        <div class="h3 font-weight-bold mb-0">RM {{ employerEPF.toFixed(2) }}</div>
                        <div class="small mt-1 opacity-75">{{ employerRate }}% of salary</div>
                      </div>
                    </CCol>
                    <CCol md="4">
                      <div class="p-3 rounded" style="background: rgba(255,255,255,0.25);">
                        <div class="small font-weight-bold mb-1 text-uppercase letter-spacing-1">Total EPF</div>
                        <div class="h3 font-weight-bold mb-0">RM {{ totalEPF.toFixed(2) }}</div>
                        <div class="small mt-1">{{ totalRate }}% combined</div>
                      </div>
                    </CCol>
                  </CRow>

                  <div class="pt-3 border-top" style="border-color: rgba(255,255,255,0.2) !important;">
                    <div class="d-flex justify-content-between mb-2">
                      <span class="opacity-75">Gross Salary:</span>
                      <strong class="font-weight-bold">RM {{ salary.toFixed(2) }}</strong>
                    </div>
                    <div class="d-flex justify-content-between mb-2">
                      <span class="opacity-75">Employee Take-Home (Net):</span>
                      <strong class="font-weight-bold">RM {{ (salary - employeeEPF).toFixed(2) }}</strong>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span class="opacity-75">Total Employer Cost:</span>
                      <strong class="font-weight-bold">RM {{ (salary + employerEPF).toFixed(2) }}</strong>
                    </div>
                  </div>
                </div>

                <!-- Empty State -->
                <div v-else class="mt-5 p-5 border text-center rounded-lg" style="border-style: dashed !important; border-width: 2px !important; border-color: #cbd5e1 !important; background: #f8fafc;">
                   <CIcon name="cil-calculator" size="2xl" class="text-muted mb-3 opacity-50"/>
                   <p class="text-muted font-weight-600 mb-0">Enter your monthly salary above to calculate EPF contributions instantly.</p>
                </div>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Right Column: Info Sidebar -->
          <CCol lg="4">
            <!-- Quick Facts Card -->
            <CCard class="border-0 shadow-sm mb-4">
              <CCardBody class="p-4">
                 <h3 class="h5 font-weight-bold text-marketing-dark mb-4">EPF Quick Facts</h3>

                 <div class="mb-4">
                   <h6 class="font-weight-bold text-marketing-dark mb-1">Employee Contribution</h6>
                   <p class="text-muted small mb-0">11% of monthly salary (below age 60)</p>
                 </div>
                 
                 <div class="mb-4 border-top pt-3">
                   <h6 class="font-weight-bold text-marketing-dark mb-1">Employer Contribution</h6>
                   <p class="text-muted small mb-1">13% (salary ≤ RM 5,000)</p>
                   <p class="text-muted small mb-0">12% (salary > RM 5,000)</p>
                 </div>

                 <div class="mb-4 border-top pt-3">
                   <h6 class="font-weight-bold text-marketing-dark mb-1">Age Exemptions</h6>
                   <ul class="text-muted small pl-3 mb-0" style="list-style-type: disc;">
                     <li><strong>Age 55-60:</strong> Can optionally opt out</li>
                     <li><strong>Age 60-75:</strong> Reduced to 4%</li>
                     <li><strong>Age 75+:</strong> Fully Exempt</li>
                   </ul>
                 </div>

                 <div class="border-top pt-4 text-center mt-5">
                   <CButton block class="btn-marketing-accent py-3 font-weight-bold mb-2" to="/signup">
                     Automate with Niagaku
                   </CButton>
                   <small class="text-muted">Calculate all payroll instantly.</small>
                 </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Educational Content -->
    <section class="py-5 bg-white">
      <CContainer>
        <div class="text-center mb-5">
           <h2 class="display-5 font-weight-bold marketing-heading mb-3">Understanding EPF (KWSP)</h2>
           <p class="lead text-muted max-w-700 mx-auto">Learn how Malaysia's mandatory retirement savings scheme works.</p>
        </div>

        <CRow>
           <CCol md="6" class="mb-4">
             <CCard class="h-100 border-0 bg-marketing-light">
               <CCardBody class="p-4">
                 <h4 class="h5 font-weight-bold mb-3">What is EPF?</h4>
                 <p class="text-muted mb-0">The Employees Provident Fund (EPF) or Kumpulan Wang Simpanan Pekerja (KWSP) is Malaysia's social security savings scheme. Both employees and employers contribute a fixed percentage of the salary monthly to build a retirement nest egg.</p>
               </CCardBody>
             </CCard>
           </CCol>
           <CCol md="6" class="mb-4">
             <CCard class="h-100 border-0 bg-marketing-light">
               <CCardBody class="p-4">
                 <h4 class="h5 font-weight-bold mb-3">EPF Benefits</h4>
                 <ul class="text-muted pl-3 mb-0">
                   <li class="mb-2">Tax relief on voluntary and mandatory contributions.</li>
                   <li class="mb-2">Annual dividend payouts (historically ~5-6%).</li>
                   <li class="mb-2">Guaranteed minimum dividend of 2.5% for Conventional Savings.</li>
                   <li>Partial early withdrawals allowed for housing, education, and health.</li>
                 </ul>
               </CCardBody>
             </CCard>
           </CCol>
        </CRow>

        <!-- Crosslinks -->
        <h4 class="text-center font-weight-bold mt-5 mb-4">Explore More Tools</h4>
        <CRow class="justify-content-center text-center">
           <CCol md="4" class="mb-3">
             <router-link to="/calculators/socso" class="text-decoration-none border p-3 rounded d-block text-marketing-dark font-weight-bold hover-bg-light transition-all">
               SOCSO Calculator <CIcon name="cil-arrow-right" class="ml-2"/>
             </router-link>
           </CCol>
           <CCol md="4" class="mb-3">
             <router-link to="/calculators/salary" class="text-decoration-none border p-3 rounded d-block text-marketing-dark font-weight-bold hover-bg-light transition-all">
               Net Salary Calculator <CIcon name="cil-arrow-right" class="ml-2"/>
             </router-link>
           </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Bottom CTA -->
    <section class="py-5 text-white text-center" style="background: linear-gradient(135deg, #1E3A5F 0%, #0F172A 100%);">
      <CContainer class="py-4">
        <h2 class="display-4 font-weight-bold mb-4 text-white">Automate Your Payroll</h2>
        <p class="lead mb-5 opacity-75 max-w-700 mx-auto" style="color: #cbd5e1;">Stop calculating manually. Niagaku automatically handles EPF, SOCSO, EIS, and generates LHDN-compliant payslips in seconds.</p>
        <div class="d-flex justify-content-center" style="gap: 1rem;">
          <CButton class="btn-marketing-accent px-5 py-3 font-weight-bold" size="lg" to="/signup">Start Free Trial</CButton>
          <CButton class="btn-outline-light-custom px-5 py-3 font-weight-bold" size="lg" to="/pricing">View Pricing</CButton>
        </div>
      </CContainer>
    </section>
  </div>
</template>

<script>
export default {
  name: 'EpfCalculator',
  metaInfo: {
    title: 'EPF Calculator Malaysia 2026 - Free KWSP Contribution Calculator',
    meta: [
      {
        name: 'description',
        content: 'Free EPF (KWSP) calculator for Malaysia. Calculate employee and employer EPF contributions instantly with latest 2026 rates. Accurate and easy to use.'
      }
    ]
  },
  data() {
    return {
      salary: 5000,
      ageRange: 'below60',
      optOut5560: false,
      employeeEPF: 0,
      employerEPF: 0,
      totalEPF: 0,
      employeeRate: 0,
      employerRate: 0,
      totalRate: 0
    }
  },
  mounted() {
    this.calculate()
  },
  methods: {
    selectAge(range) {
      this.ageRange = range
      this.optOut5560 = false
      this.calculate()
    },
    calculate() {
      if (!this.salary || this.salary <= 0) {
        this.employeeEPF = 0
        this.employerEPF = 0
        this.totalEPF = 0
        this.employeeRate = 0
        this.employerRate = 0
        this.totalRate = 0
        return
      }

      const monthlySalary = parseFloat(this.salary)

      // Employee Contribution
      if (this.ageRange === 'below60' && !this.optOut5560) {
        this.employeeRate = 11
        this.employeeEPF = monthlySalary * 0.11
      } else if (this.ageRange === '60to75') {
        this.employeeRate = 4
        this.employeeEPF = monthlySalary * 0.04
      } else {
        this.employeeRate = 0
        this.employeeEPF = 0
      }

      // Employer Contribution
      if (this.ageRange === 'below60' && !this.optOut5560) {
        if (monthlySalary <= 5000) {
          this.employerRate = 13
          this.employerEPF = monthlySalary * 0.13
        } else {
          this.employerRate = 12
          this.employerEPF = monthlySalary * 0.12
        }
      } else if (this.ageRange === '60to75') {
        this.employerRate = 4
        this.employerEPF = monthlySalary * 0.04
      } else {
        this.employerRate = 0
        this.employerEPF = 0
      }

      this.totalEPF = this.employeeEPF + this.employerEPF
      this.totalRate = this.employeeRate + this.employerRate
    }
  }
}
</script>

<style scoped>
.hover-bg-light:hover {
  background-color: #f8fafc;
}
.transition-all {
  transition: all 0.2s ease-in-out;
}
.max-w-700 {
  max-width: 700px;
}
.letter-spacing-1 {
  letter-spacing: 1px;
}
.opacity-75 {
  opacity: 0.75;
}
.opacity-50 {
  opacity: 0.5;
}
</style>
